import { Check } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { BookCallButton } from "./CTA";

export const TIERS = [
  {
    name: "Foundation",
    price: "₹20,000",
    per: "/month",
    desc: "For agencies establishing a professional acquisition system.",
    features: [
      "10 static posts / month",
      "2 carousels / month",
      "2 reels / month (client-supplied footage)",
      "2 Meta ad sets, 4 ad creatives",
      "Meta Ads management & optimization",
      "2 strategy calls / month",
      "Monthly performance report",
    ],
    featured: false,
    testid: "pricing-foundation",
  },
  {
    name: "Growth",
    price: "₹40,000",
    per: "/month",
    desc: "For agencies actively investing in lead generation.",
    features: [
      "20 static posts / month",
      "4 carousels / month",
      "6 reels / month (client-supplied footage)",
      "4 Meta ad sets, 12 ad creatives",
      "Meta Ads management & optimization",
      "2 strategy calls / month",
      "Monthly performance report",
      "Google Ads",
      "Creative strategy",
      "Retargeting",
      "Landing page recommendations",
      "WhatsApp lead flow",
      "Email nurture strategy",
      "Performance optimization",
    ],
    featured: true,
    testid: "pricing-growth",
  },
  {
    name: "Scale",
    price: "₹60,000",
    per: "/month",
    desc: "For agencies looking for a broader acquisition system.",
    features: [
      "30 static posts / month",
      "6 carousels / month",
      "10 reels / month (client-supplied footage)",
      "6 Meta ad sets, 15 ad creatives",
      "Meta Ads management & optimization",
      "4 strategy calls / month",
      "Monthly performance report",
      "Multi-channel strategy",
      "Google Ads",
      "WhatsApp marketing",
      "Email marketing",
      "Advanced funnel strategy",
      "Creative testing",
      "Performance optimization",
      "Strategic reporting",
    ],
    featured: false,
    testid: "pricing-scale",
  },
];

export const PricingCards = ({ showNotes = true, showHeading = false }) => (
  <section className="bg-cream py-24 md:py-32 relative overflow-hidden" data-testid="pricing-section">
    <div className="absolute inset-0 geo-pattern opacity-[0.04]" />
    <div className="relative max-w-7xl mx-auto px-6">
      {showHeading && (
        <Reveal className="mb-16 max-w-3xl">
          <SectionTag className="mb-6">Pricing</SectionTag>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-charcoal leading-[1.05]">
            Choose the Right Level of Marketing Support for Your Agency.
          </h2>
        </Reveal>
      )}
      <div className="grid md:grid-cols-3 gap-6">
        {TIERS.map((tier, i) => (
          <Reveal key={tier.name} delay={i * 0.1} className="h-full">
            <div
              data-testid={tier.testid}
              className={`h-full flex flex-col p-8 md:p-10 border transition-colors duration-300 ${
                tier.featured
                  ? "bg-charcoal text-cream border-gold/50 shadow-[0_24px_60px_rgba(45,42,39,0.25)]"
                  : "bg-white text-charcoal border-charcoal/10 hover:border-gold/50"
              }`}
            >
              <div
                className={`text-xs font-semibold tracking-[0.25em] uppercase ${
                  tier.featured ? "text-gold" : "text-tan"
                }`}
              >
                {tier.name}
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-bold tracking-tighter">
                  {tier.price}
                </span>
                <span
                  className={`text-sm ${tier.featured ? "text-cream/50" : "text-charcoal/50"}`}
                >
                  {tier.per}
                </span>
              </div>
              <p
                className={`mt-4 text-sm leading-relaxed ${
                  tier.featured ? "text-cream/60" : "text-charcoal/60"
                }`}
              >
                {tier.desc}
              </p>
              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      size={15}
                      strokeWidth={2}
                      className={`mt-0.5 shrink-0 ${tier.featured ? "text-gold" : "text-tan"}`}
                    />
                    <span className={tier.featured ? "text-cream/80" : "text-charcoal/75"}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <BookCallButton
                  label="Discuss This Plan"
                  testid={`${tier.testid}-cta`}
                  dark={tier.featured}
                  className="w-full"
                />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      {showNotes && (
        <Reveal delay={0.2}>
          <div
            className="mt-12 border border-gold/30 bg-white/60 p-6 md:p-8 space-y-2"
            data-testid="pricing-notes"
          >
            <p className="text-sm text-charcoal/70 leading-relaxed">
              All reels are created using footage, information and media
              supplied by the client. For Foundation, we recommend a starting
              Meta advertising budget of ₹10,000 per month. Growth and Scale
              advertising budgets are determined according to your goals,
              market, package economics, campaign requirements and desired
              scale.
            </p>
            <p className="text-sm font-semibold text-charcoal leading-relaxed">
              Advertising spend is paid to the advertising platforms and is
              separate from Nexus Catalyst's service fees.
            </p>
          </div>
        </Reveal>
      )}
    </div>
  </section>
);
